import { IconButton } from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { getNote, NOTE_MAX_LENGTH, saveNote, type NoteView } from "@/api/trainingNote";
import { Editor, useEditorController } from "@/components/editor";
import { resolveEvalLoadErrorMessage } from "@/modules/evaluation/public";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { formatEvalDateTime } from "./evalRunFormat";
import styles from "./EvalRunAnnotationEditor.module.css";

type Props = {
  evalRunId: string;
};

export function EvalRunAnnotationEditor({ evalRunId }: Props): ReactNode {
  const [note, setNote] = useState<NoteView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [tags, setTags] = useState<Record<string, string>>({});
  const [tagKey, setTagKey] = useState("");
  const [tagValue, setTagValue] = useState("");
  const evalRunIdRef = useRef(evalRunId);
  evalRunIdRef.current = evalRunId;
  const editor = useEditorController({
    value: note?.note ?? null,
    onSave: async (draft) => {
      const savingEvalRunId = evalRunId;
      try {
        // 본문을 비웠을 때 레코드를 지울지 태그만 남길지는 saveNote 가 정한다. 이 화면에서는
        // 태그가 주요 데이터라서 태그만 남는 경우가 정상이다. 그래서 여기서 따로 판정하지 않는다.
        const view = await saveNote("eval_run", savingEvalRunId, { note: draft, tags });
        if (evalRunIdRef.current !== savingEvalRunId) return { ok: false };
        setNote(view);
        setTags(stringTags(view.tags));
        setEditing(false);
        return { ok: true, value: view.note ?? "" };
      } catch (saveError) {
        return { ok: false, message: resolveEvalLoadErrorMessage(saveError) };
      }
    },
  });
  const replaceEditorValue = editor.replace;

  useEffect(() => {
    let disposed = false;
    replaceEditorValue(null);
    setNote(null);
    setEditing(false);
    setError(null);
    void getNote("eval_run", evalRunId)
      .then((view) => {
        if (disposed) return;
        setNote(view);
        setTags(stringTags(view.tags));
      })
      .catch((loadError: unknown) => {
        if (!disposed) setError(resolveEvalLoadErrorMessage(loadError));
      });
    return () => {
      disposed = true;
    };
  }, [evalRunId, replaceEditorValue]);

  const tagEntries = useMemo(() => Object.entries(tags), [tags]);

  if (note === null) {
    return error === null ? (
      <LoadingState size="sm">불러오는 중…</LoadingState>
    ) : (
      <EmptyState size="sm" tone="error">
        {error}
      </EmptyState>
    );
  }

  function beginEditing(): void {
    replaceEditorValue(note?.note ?? "");
    setTags(stringTags(note?.tags ?? {}));
    setEditing(true);
  }

  function addTag(): void {
    if (editor.inputDisabled) return;
    const key = tagKey.trim();
    if (key === "") return;
    setTags((current) => ({ ...current, [key]: tagValue.trim() }));
    setTagKey("");
    setTagValue("");
  }

  function save(): void {
    setError(null);
    void editor.save();
  }

  if (!editing) {
    const entries = Object.entries(note.tags);
    const hasAnnotation = note.note !== null || entries.length > 0;
    return (
      <div className={styles.card} data-testid={`annotation-editor-eval_run-${evalRunId}`}>
        <div className={styles.editorHeader}>
          <h4 className={styles.editorTitle}>연구 기록</h4>
          <IconButton
            text={hasAnnotation ? "편집" : "기록 추가"}
            size="sm"
            variant="surface"
            onClick={beginEditing}
          />
        </div>
        {!hasAnnotation ? (
          <EmptyState size="sm">
            아직 기록이 없습니다. 이 실행이 왜 champion 인지, 왜 실패했는지, 다음에 무엇을 바꿀지
            남겨 두면 나중에 비교하기 쉽습니다.
          </EmptyState>
        ) : (
          <>
            {note.note ? (
              <pre className={styles.note} data-testid="annotation-readonly">
                {note.note}
              </pre>
            ) : null}
            {entries.length > 0 ? (
              <div className={styles.tags}>
                {entries.map(([key, value]) => (
                  <span key={key} className={styles.tag}>
                    {key}={formatTagValue(value)}
                  </span>
                ))}
              </div>
            ) : null}
            {note.updated_at ? (
              <span className={styles.timestamp}>수정: {formatEvalDateTime(note.updated_at)}</span>
            ) : null}
          </>
        )}
      </div>
    );
  }

  return (
    <div className={styles.card} data-testid={`annotation-editor-eval_run-${evalRunId}`}>
      <label className={styles.label} htmlFor={`eval-note-${evalRunId}`}>
        기록
      </label>
      <Editor
        controller={editor}
        id={`eval-note-${evalRunId}`}
        className={styles.textarea}
        maxLength={NOTE_MAX_LENGTH}
        rows={6}
        placeholder="왜 champion 인지 / 왜 실패했는지 / 다음에 무엇을 바꿀지"
        data-testid="annotation-note-input"
      />
      <span className={styles.tagLabel}>Tag</span>
      {tagEntries.length > 0 ? (
        <div className={styles.tags}>
          {tagEntries.map(([key, value]) => (
            <button
              key={key}
              type="button"
              className={styles.tagButton}
              aria-label={`Tag ${key} 제거`}
              data-testid={`annotation-tag-chip-${key}`}
              disabled={editor.inputDisabled}
              onClick={() =>
                setTags((current) => {
                  const next = { ...current };
                  delete next[key];
                  return next;
                })
              }
            >
              {key}={value} ×
            </button>
          ))}
        </div>
      ) : null}
      <div className={styles.tagComposer}>
        <input
          className={styles.input}
          value={tagKey}
          placeholder="key"
          aria-label="Tag key"
          data-testid="annotation-tag-key-input"
          disabled={editor.inputDisabled}
          onChange={(event) => setTagKey(event.target.value)}
        />
        <input
          className={styles.input}
          value={tagValue}
          placeholder="value"
          aria-label="Tag value"
          data-testid="annotation-tag-value-input"
          disabled={editor.inputDisabled}
          onChange={(event) => setTagValue(event.target.value)}
        />
        <IconButton
          text="추가"
          size="sm"
          variant="surface"
          disabled={editor.inputDisabled}
          onClick={addTag}
        />
      </div>
      {error !== null || editor.state.status === "error" ? (
        <p className={styles.error}>
          {error ?? (editor.state.status === "error" ? editor.state.message : null)}
        </p>
      ) : null}
      <div className={styles.actions}>
        <span className={styles.counter}>
          {editor.value.length}/{NOTE_MAX_LENGTH}
        </span>
        <IconButton
          text="취소"
          size="sm"
          variant="surface"
          disabled={editor.saving}
          onClick={() => setEditing(false)}
        />
        <IconButton
          text={editor.saving ? "저장 중…" : "저장"}
          size="sm"
          variant="solid"
          tone="primary"
          disabled={editor.inputDisabled}
          onClick={save}
        />
      </div>
    </div>
  );
}

function stringTags(tags: Record<string, unknown>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(tags).map(([key, value]) => [key, formatTagValue(value)]),
  );
}

function formatTagValue(value: unknown): string {
  if (typeof value === "string") return value;
  return JSON.stringify(value) ?? String(value);
}
